/**
 * Resolves who the Supervision UI applies to:
 * - Supervisors: active practitioners who list this user as supervising practitioner.
 * - Providers: only the signed-in clinician's own queue / tasks.
 * - Compliance tab: supervisors only, and FEATURE_VISIBILITY.supervisionCompliance.
 */

import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useCurrentUserPractitioner } from '../../../hooks/useCurrentUserPractitioner';
import { findCanonicalSuperviseeIds } from '../../../lib/supervision/supervisionRelationships';
import { practitionerDisplayName } from '../../../lib/supervision/supervisionDisplay';
import type { Role } from '../../../pages/users/types/rbac';
import { FEATURE_VISIBILITY } from '../../../config/featureVisibility';

export interface SupervisionScope {
  /** RBAC role from Medplum profile. */
  role: Role;
  /** True when Compliance tab should be shown (supervisor role only). */
  showComplianceTab: boolean;
  /** Current user's Practitioner id, when resolved. */
  viewerPractitionerId: string | undefined;
  /** Practitioners supervised by the viewer (empty until loaded for supervisors). */
  superviseePractitionerIds: string[];
  /** True while supervisee list is loading (live + supervisor only). */
  superviseeLoading: boolean;
  /** True when scope can be applied (RBAC + practitioner resolved; supervisees loaded if supervisor). */
  scopeReady: boolean;
  /** Whether QR/Task rows for this clinician should appear in Supervision for the current user. */
  isClinicianInScope: (clinicianId: string) => boolean;
  /** Whether a QR author reference is in scope (pass Practitioner id from QR.author). */
  isAuthorInScope: (authorPractitionerId: string | undefined) => boolean;
  /**
   * Practitioner ids to load missing-document appointments/tasks for: supervisees plus self
   * (supervisor) or the signed-in clinician (provider). Empty for other roles or before ids resolve.
   * Cosign queue still uses superviseePractitionerIds / isClinicianInScope (supervisees only).
   */
  scopedClinicianIds: string[];
  /** Supervisees with display names for provider filters (independent of loaded queue rows). */
  superviseeOptions: { id: string; name: string }[];
}

export function useSupervisionScope(): SupervisionScope {
  const { role, ready: rbacReady } = usePermissions();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const medplum = useMedplum();

  const viewerPractitionerId = practitioner?.id;
  const showComplianceTab = role === 'supervisor' && FEATURE_VISIBILITY.supervisionCompliance;
  const isSupervisorRole = role === 'supervisor';
  const isOwnPanelRole = role === 'provider';

  const [superviseePractitionerIds, setSuperviseePractitionerIds] = useState<string[]>([]);
  const [superviseeOptions, setSuperviseeOptions] = useState<{ id: string; name: string }[]>([]);
  const [superviseeLoading, setSuperviseeLoading] = useState(false);

  useEffect(() => {
    if (!rbacReady || !isSupervisorRole || !viewerPractitionerId) {
      setSuperviseePractitionerIds([]);
      setSuperviseeOptions([]);
      setSuperviseeLoading(false);
      return;
    }

    let cancelled = false;
    setSuperviseeLoading(true);
    (async () => {
      try {
        medplum.invalidateSearches('Practitioner');
        const all = await medplum.searchResources('Practitioner', { _count: '500' });
        if (cancelled) return;
        const superviseeIds = findCanonicalSuperviseeIds(all, viewerPractitionerId);
        setSuperviseePractitionerIds(superviseeIds);
        setSuperviseeOptions(
          superviseeIds.map((id) => {
            const p = all.find((row) => row.id === id);
            return { id, name: p ? practitionerDisplayName(p) : id };
          }),
        );
      } catch {
        if (!cancelled) {
          setSuperviseePractitionerIds([]);
          setSuperviseeOptions([]);
        }
      } finally {
        if (!cancelled) setSuperviseeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, rbacReady, isSupervisorRole, viewerPractitionerId]);

  const scopeReady = useMemo(() => {
    if (!rbacReady || practitionerLoading) return false;
    if (isSupervisorRole) {
      return !superviseeLoading;
    }
    if (isOwnPanelRole) {
      return true;
    }
    /** Other roles (e.g. admin): show empty supervision lists once profile is ready. */
    return rbacReady && !practitionerLoading;
  }, [
    rbacReady,
    practitionerLoading,
    viewerPractitionerId,
    isOwnPanelRole,
    isSupervisorRole,
    superviseeLoading,
  ]);

  const isClinicianInScope = useCallback(
    (clinicianId: string): boolean => {
      if (!clinicianId) return false;
      if (isSupervisorRole) return superviseePractitionerIds.includes(clinicianId);
      if (isOwnPanelRole && viewerPractitionerId) return clinicianId === viewerPractitionerId;
      return false;
    },
    [isSupervisorRole, isOwnPanelRole, viewerPractitionerId, superviseePractitionerIds],
  );

  const isAuthorInScope = useCallback(
    (authorPractitionerId: string | undefined): boolean => {
      if (!authorPractitionerId) return false;
      return isClinicianInScope(authorPractitionerId);
    },
    [isClinicianInScope],
  );

  const scopedClinicianIds = useMemo(() => {
    if (isSupervisorRole) {
      const ids = [...superviseePractitionerIds];
      if (viewerPractitionerId && !ids.includes(viewerPractitionerId)) {
        ids.push(viewerPractitionerId);
      }
      return ids;
    }
    if (isOwnPanelRole && viewerPractitionerId) return [viewerPractitionerId];
    return [];
  }, [isSupervisorRole, superviseePractitionerIds, isOwnPanelRole, viewerPractitionerId]);

  return {
    role,
    showComplianceTab,
    viewerPractitionerId,
    superviseePractitionerIds,
    superviseeLoading,
    scopeReady,
    isClinicianInScope,
    isAuthorInScope,
    scopedClinicianIds,
    superviseeOptions,
  };
}
